import React, { useState, useEffect } from "react";

const PaymentConformation = () => {
  const [payloadData, setPayloadData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const fetchPaymentPayload = () => {
      let rawData = {};

      // 1️⃣ Try to get data from form POST (if payment gateway posts here)
      const forms = document.forms;
      if (forms.length > 0) {
        const formData = new FormData(forms[0]);
        for (let [key, value] of formData.entries()) {
          rawData[key] = value;
        }
      } else {
        // 2️⃣ Fallback: read from URL query params
        const params = new URLSearchParams(window.location.search);
        for (let [key, value] of params.entries()) {
          rawData[key] = value;
        }
      }

      // 3️⃣ Map raw data into structured format
      const structuredPayload = {
        responseCode: rawData["Response Code"] || rawData.ResponseCode || "",
        uniqueRefNumber: rawData["Unique Ref Number"] || rawData.UniqueRefNumber || "",
        serviceTaxAmount: rawData["Service Tax Amount"] || rawData.ServiceTaxAmount || "",
        processingFeeAmount: rawData["Processing Fee Amount"] || rawData.ProcessingFeeAmount || "",
        totalAmount: rawData["Total Amount"] || rawData.TotalAmount || "",
        transactionAmount: rawData["Transaction Amount"] || rawData.TransactionAmount || "",
        transactionDate: rawData["Transaction Date"] || rawData.TransactionDate || "",
        interchangeValue: rawData["Interchange Value"] || rawData.InterchangeValue || "",
        tdr: rawData["TDR"] || rawData.TDR || "",
        paymentMode: rawData["Payment Mode"] || rawData.PaymentMode || "",
        subMerchantId: rawData["SubMerchantId"] || rawData.SubMerchantId || "",
        referenceNo: rawData["ReferenceNo"] || rawData.ReferenceNo || "",
        merchantId: rawData["ID"] || rawData.ID || "",
        rs: rawData["RS"] || "",
        tps: rawData["TPS"] || "",
        mandatoryFields: rawData["mandatory fields"] || rawData.mandatoryFields || "",
        optionalFields: rawData["optional fields"] || rawData.optionalFields || "",
        rsv: rawData["RSV"] || ""
      };

      setPayloadData(structuredPayload);
      setIsLoading(false);
    };

    fetchPaymentPayload();
  }, []);

  const handleRedirect = () => {
    setRedirecting(true);
    setTimeout(() => {
      const confirmationPage = document.createElement('div');
      confirmationPage.style.minHeight = '100vh';
      confirmationPage.style.background = 'linear-gradient(to br, #f0fdf4, #f0fff4)';
      confirmationPage.style.display = 'flex';
      confirmationPage.style.alignItems = 'center';
      confirmationPage.style.justifyContent = 'center';
      confirmationPage.innerHTML = `
        <div style="text-align: center; padding: 2rem; background: white; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.1);">
          <div style="width: 80px; height: 80px; background: #10b981; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem;">
            <svg style="width: 40px; height: 40px; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h1 style="color: #16a34a; font-size: 2rem; margin-bottom: 1rem;">Transaction Confirmed</h1>
          <p style="color: #6b7280; margin-bottom: 2rem;">Your payment has been successfully processed</p>
          <button onclick="window.location.reload()" style="padding: 0.75rem 1.5rem; background: #3b82f6; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600;">Back to Dashboard</button>
        </div>
      `;
      document.body.innerHTML = '';
      document.body.appendChild(confirmationPage);
    }, 1500);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Processing payment response...</p>
        </div>
      </div>
    );
  }

  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-emerald-50">
        <div className="text-center">
          <div className="animate-pulse rounded-full h-16 w-16 bg-green-500 mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-green-600 text-lg font-medium">Redirecting to confirmation page...</p>
          <p className="text-gray-500 mt-2">Please wait a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold mb-6">Transaction Summary</h1>

        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Transaction Amount:</span>
              <span className="font-semibold text-gray-900">₹{payloadData.totalAmount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Payment Mode:</span>
              <span className="font-semibold text-gray-900">{payloadData.paymentMode}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Transaction Date:</span>
              <span className="font-semibold text-gray-900">{payloadData.transactionDate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Reference Number:</span>
              <span className="font-semibold text-gray-900">{payloadData.referenceNo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="font-semibold text-green-600">Success</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-xl font-semibold mb-4">Full Payload</h2>
          <pre className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-96">{JSON.stringify(payloadData, null, 2)}</pre>
        </div>

        <div className="mt-6 flex space-x-4">
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-gray-200 rounded">Back</button>
          <button onClick={handleRedirect} className="px-4 py-2 bg-indigo-600 text-white rounded">Go to Confirmation</button>
        </div>
      </main>
    </div>
  );
};

export default PaymentConformation;
